import { makeStyles } from "tss-react/mui";
import { Grid, Typography, Table, TableBody, TableRow, TableCell, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEntity } from "@emberly/zenith-client";
import { OrderEnumsLists } from "../../../../../common/constants";
import { FullTimeView } from "../../../core/Time";
import { useCallback } from "react";
import { UpdateCustomer } from "../../../../../common/orders";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useNotification } from "../../../../../providers/NotificationProvider";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      width: "100%",
      height: "auto",
      background: theme.palette.background.grey,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2)
    },
    table: {
      width: "100%",
      "& td": {
        border: "none",
        padding: theme.spacing(1),
        paddingLeft: 0,
        paddingBottom: theme.spacing(0.25),
        paddingTop: theme.spacing(0.25),
      },
    },
    textWithButton: {
      position: "relative",
    },
    reloadButton: {
      position: "absolute",
      right: "-17px",
      top: "2px",
      width: "16x",
      height: "16px",
      padding: 0,
      "& svg": {
        fontSize: "16px",
        padding: 0,
      }
    }
  })
);

export default function InvoiceReadyInformationCard(props) {
  const { customerEntity, canUpdateCustomerData } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { entity, updateEntityField } = useEntity();
  const { invoice, comment, reference } = entity.payment;
  const customer = entity.customer;
  const billing = customer.billing;
  const { snackbar } = useNotification();

  const onUpdateCustomer = useCallback(() => {
    UpdateCustomer(customerEntity, updateEntityField);
    snackbar(t("order:invoiceInformation:updatedCustomerData"));
  }, [customerEntity, updateEntityField, t, snackbar]);

  return (
    <div className={classes.root}>
      <Grid container alignItems="center">

        <Grid item xs={12}>
          <Typography variant="subtitle2">
            {t("order:invoiceInformation:title")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Table className={classes.table}>
            <TableBody>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:state")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{t(`order:enums:invoiceState:${OrderEnumsLists.InvoiceState[invoice.state]}`)}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:date")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left"><FullTimeView time={invoice?.stateChangedDate} /></Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:person")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{entity.payment.sender.name}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" className={classes.textWithButton}>
                    {t("order:invoiceInformation:invoice")}
                    {
                      !!customerEntity && !!entity && !!customerEntity && canUpdateCustomerData ? (
                        <IconButton size="small" className={classes.reloadButton} onClick={onUpdateCustomer}>
                          <RefreshIcon size="small" />
                        </IconButton>
                      ) : null
                    }
                  </Typography>
                </TableCell>

              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:orderDate")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left"><FullTimeView time={invoice?.orderDate} isDate /></Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("entity:location:address")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{billing.address},{!!billing.address2 ? ` ${billing.address2},` : ""} {billing.postNumber} {billing.area}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:email")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{billing.email || "-"}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:contactPerson")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{invoice.contactPerson?.name || "-"}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("entity:contact:organizationNumber")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{customer.organizationNumber || "-"}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:customerNumber")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{customer.customerNumber || "-"}</Typography>
                </TableCell>
              </TableRow>


              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("common:comment")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{comment || "-"}</Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{t("order:invoiceInformation:reference")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" textAlign="left">{reference || "-"}</Typography>
                </TableCell>
              </TableRow>

            </TableBody>
          </Table>
        </Grid>

      </Grid>
    </div>
  );
}
